import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { Badge } from "../Badge";
import { FileText, Eye, CalendarCheck, Users, HeartHandshake, CalendarDays, CalendarClock, UserX, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <p className="text-slate-500 font-semibold">Loading dashboard…</p>;

  const cards = [
    { label: "New Referrals", value: stats.new_referrals, icon: FileText, testId: "stat-new-referrals" },
    { label: "Referrals Under Review", value: stats.under_review, icon: Eye, testId: "stat-under-review" },
    { label: "New Consultations", value: stats.new_consultations, icon: CalendarCheck, testId: "stat-new-consultations" },
    { label: "Active Patients", value: stats.active_patients, icon: Users, testId: "stat-active-patients" },
    { label: "Active Caregivers", value: stats.active_caregivers, icon: HeartHandshake, testId: "stat-active-caregivers" },
    { label: "Today's Visits", value: stats.today_visits, icon: CalendarDays, testId: "stat-today-visits" },
    { label: "Upcoming Visits", value: stats.upcoming_visits, icon: CalendarClock, testId: "stat-upcoming-visits" },
    { label: "Unassigned Visits", value: stats.unassigned_visits, icon: UserX, testId: "stat-unassigned-visits" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-3xl md:text-4xl text-brand-navy">Dashboard</h1>
      <p className="mt-2 text-[15px] text-slate-600">Overview of referrals, patients, caregivers, and scheduled visits.</p>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testId} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_8px_30px_rgb(11,27,61,0.04)]">
            <div className="flex items-center justify-between">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal">
                <c.icon className="h-5 w-5" strokeWidth={1.6} />
              </span>
              <span className="font-display text-3xl font-semibold text-brand-navy">{c.value}</span>
            </div>
            <p className="mt-3 text-sm font-bold text-slate-600">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl text-brand-navy">Recent Patient Referrals</h2>
            <Link to="/portal/referrals" data-testid="dash-view-referrals" className="text-sm font-bold text-brand-teal hover:text-brand-navy inline-flex items-center gap-1">View all <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <ul className="mt-4 space-y-3">
            {stats.recent_referrals.length === 0 && <li className="text-sm text-slate-500">No referrals yet.</li>}
            {stats.recent_referrals.map((r) => (
              <li key={r.id}>
                <Link to={`/portal/referrals/${r.id}`} data-testid={`dash-referral-${r.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-4 py-3 hover:border-brand-teal/40 transition-colors duration-300">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-brand-navy truncate">{r.patient_name}</p>
                    <p className="text-xs text-slate-500">{new Date(r.submitted_at).toLocaleDateString()}</p>
                  </div>
                  <Badge kind="referral" value={r.status} />
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl text-brand-navy">Upcoming Scheduled Visits</h2>
            <Link to="/portal/schedule" data-testid="dash-view-schedule" className="text-sm font-bold text-brand-teal hover:text-brand-navy inline-flex items-center gap-1">Schedule <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <ul className="mt-4 space-y-3">
            {stats.upcoming_list.length === 0 && <li className="text-sm text-slate-500">No upcoming visits scheduled.</li>}
            {stats.upcoming_list.map((v) => (
              <li key={v.id} data-testid={`dash-visit-${v.id}`} className="rounded-xl border border-slate-100 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-bold text-brand-navy truncate">{v.patient_name}</p>
                  <Badge kind="visit" value={v.status} />
                </div>
                <p className="mt-1 text-xs text-slate-500">{v.date} · {v.start_time} – {v.end_time} · {v.caregiver_name}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-display text-xl text-brand-navy">Recent Activity</h2>
          <ul className="mt-4 space-y-3">
            {stats.recent_activity.length === 0 && <li className="text-sm text-slate-500">No recent activity.</li>}
            {stats.recent_activity.map((a) => (
              <li key={a.id} className="text-sm text-slate-600 border-l-2 border-brand-teal/40 pl-3">
                {a.text}
                <span className="block text-xs text-slate-400 mt-0.5">{new Date(a.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
